import { Logger } from '@hot-club-http/client/utils'

import type { EventsName } from './types'

/**
 * HotClubHttpEvents is a utility class designed for managing and dispatching custom events
 * in the browser environment. It provides a standardized way to communicate between
 * different parts of an application using custom events.
 *
 * It includes the following static methods:
 *
 * - `addListener`: Attaches a callback function to listen for specific custom events.
 * - `removeListener`: Detaches a previously attached callback function from a custom event.
 *
 */
class HotClubHttpEvents {
  static emit(eventName: EventsName, detail: unknown): void {
    const event = new CustomEvent(eventName, { detail })
    window.dispatchEvent(event)
  }

  /**
   * Adds a listener for a specific custom event.
   * The listener will be called whenever the specified event is emitted.
   *
   * @param {EventsName} eventName - **(Required)** - The name of the event to listen for.
   * @param {(event: CustomEvent) => void} callback - **(Required)** - The function to execute when the event is triggered.
   * The function receives the event object as a parameter.
   *
   * @throws Will throw an error if adding the listener fails.
   *
   * @example
   * HotClubHttpEvents.addListener('REQUEST_SUCCESS', (event) => {
   *   console.log('Request succeeded:', event.detail);
   * });
   */
  static addListener(eventName: EventsName, callback: (event: CustomEvent) => void): void {
    try {
      window.addEventListener(eventName, callback as EventListener)
    } catch (e) {
      Logger.error('addListener', { error: e?.message })
      throw e
    }
  }

  /**
   * Removes a listener for a specific custom event.
   * This is important for preventing memory leaks and ensuring cleanup when the listener is no longer needed.
   *
   * @param {EventsName} eventName - **(Required)** - The name of the event to stop listening for.
   * @param {(event: CustomEvent) => void} callback - **(Required)** - The function that was previously attached as a listener.
   * The same reference must be provided to remove the listener.
   *
   * @throws Will throw an error if removing the listener fails.
   *
   * @example
   * const onRequestSuccess = (event) => {
   *   console.log('Request succeeded:', event.detail);
   * };
   *
   * HotClubHttpEvents.addListener('REQUEST_SUCCESS', onRequestSuccess);
   * HotClubHttpEvents.removeListener('REQUEST_SUCCESS', onRequestSuccess);
   */
  static removeListener(eventName: EventsName, callback: (event: CustomEvent) => void): void {
    try {
      window.removeEventListener(eventName, callback as EventListener)
    } catch (e) {
      Logger.error('removeListener', { error: e?.message })
      throw e
    }
  }
}

export default HotClubHttpEvents
